/**
 * PMPipelineV2 - Redesigned PM Pipeline
 *
 * Master-detail layout with 3 priority sections:
 * 1. Kicked Off (In Production) - highest priority
 * 2. Won - Needs Kickoff - middle priority
 * 3. Pending (Not Won) - lowest priority, collapsed
 */

import { useState, useEffect, useCallback } from "react";

// Format date for display
function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// Calculate days until ship date
function getDaysUntilShip(shipDate) {
  if (!shipDate) return null;
  const ship = new Date(shipDate);
  const today = new Date();
  const diffTime = ship - today;
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

// Format currency
function formatCurrency(value) {
  if (!value) return "$0";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

// Project card component (left side list)
function ProjectCard({ project, isSelected, onClick }) {
  const daysLeft = getDaysUntilShip(project.shipDate);
  const isOverdue = daysLeft !== null && daysLeft < 0;
  const isUrgent = daysLeft !== null && daysLeft >= 0 && daysLeft <= 3;

  return (
    <div
      onClick={onClick}
      className={`
        px-3 py-2.5 cursor-pointer border-l-4 transition-colors
        ${isSelected
          ? "bg-purple-50 border-l-purple-500"
          : "bg-white border-l-transparent hover:bg-gray-50"
        }
      `}
    >
      {/* Quote number + Customer */}
      <div className="flex items-center gap-2 mb-1">
        <span className="font-mono text-sm font-semibold text-purple-600">
          {project.quoteNumber}
        </span>
        {project.orderNumber && (
          <span className="text-xs text-gray-400">({project.orderNumber})</span>
        )}
        <span className="text-gray-300">|</span>
        <span className="text-sm font-medium text-gray-800 truncate">
          {project.customerName}
        </span>
      </div>

      {/* Project name */}
      <div className="text-sm text-gray-600 truncate mb-1.5">
        {project.projectName || "—"}
      </div>

      {/* Meta row */}
      <div className="flex items-center gap-3 text-xs text-gray-500">
        {/* Ship date */}
        {project.shipDate && (
          <span className={
            isOverdue ? "text-red-600 font-medium" :
            isUrgent ? "text-orange-600 font-medium" :
            "text-gray-500"
          }>
            Ship: {formatDate(project.shipDate)}
            {daysLeft !== null && (
              <span className="ml-1">
                ({isOverdue ? `${Math.abs(daysLeft)}d late` : `${daysLeft}d`})
              </span>
            )}
          </span>
        )}

        {/* SKU count */}
        {project.totalSkus > 0 && (
          <span>{project.totalSkus} SKU{project.totalSkus !== 1 ? "s" : ""}</span>
        )}

        {/* PM badge */}
        {project.assignedPmName ? (
          <span className="text-green-600">PM: {project.assignedPmName.split(" ")[0]}</span>
        ) : (
          <span className="text-orange-500">No PM</span>
        )}

        {/* Needs ship date warning */}
        {project.needsShipDate && (
          <span className="text-red-500">Needs ship date</span>
        )}
      </div>
    </div>
  );
}

// Section header component
function SectionHeader({ section, expanded, onToggle }) {
  const colorMap = {
    ready_to_work: { bg: "bg-green-50", text: "text-green-700", badge: "bg-green-100 text-green-700" },
    won_awaiting_kickoff: { bg: "bg-yellow-50", text: "text-yellow-700", badge: "bg-yellow-100 text-yellow-700" },
    pending: { bg: "bg-blue-50", text: "text-blue-700", badge: "bg-blue-100 text-blue-700" },
    // Legacy support
    kicked_off: { bg: "bg-green-50", text: "text-green-700", badge: "bg-green-100 text-green-700" },
    won_needs_kickoff: { bg: "bg-yellow-50", text: "text-yellow-700", badge: "bg-yellow-100 text-yellow-700" },
  };
  const colors = colorMap[section.id] || colorMap.pending;

  return (
    <button
      onClick={onToggle}
      className={`w-full px-3 py-2.5 flex items-center justify-between ${colors.bg} hover:brightness-95 transition-all`}
    >
      <div className="flex items-center gap-2">
        <span className={`font-semibold ${colors.text}`}>{section.name}</span>
        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${colors.badge}`}>
          {section.count}
        </span>
      </div>
      <svg
        className={`w-4 h-4 text-gray-500 transition-transform ${expanded ? "rotate-180" : ""}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </button>
  );
}

// Fundamentals checklist component for Pipeline
function FundamentalsChecklist({ fundamentals, onUpdate, disabled, onSendBackToSales }) {
  const items = [
    { key: 'kickoff_received', label: 'Kickoff Email Received', description: 'Sales has sent the kickoff email' },
    { key: 'folder_created', label: 'Folder Created', description: 'Project folder exists on server' },
    { key: 'artwork_received', label: 'Artwork Received', description: 'Artwork files saved on server' },
  ];

  const allChecked = items.every(item => fundamentals[item.key]);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-900">Fundamentals Checklist</h3>
        {allChecked ? (
          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">Ready</span>
        ) : (
          <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs font-medium rounded">Incomplete</span>
        )}
      </div>
      <p className="text-xs text-gray-500 mb-3">Verify these before assigning the project</p>
      <div className="space-y-2">
        {items.map(item => (
          <label key={item.key} className={`flex items-start gap-3 p-2 rounded cursor-pointer transition-colors ${
            fundamentals[item.key] ? 'bg-green-50' : 'hover:bg-gray-50'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
            <input
              type="checkbox"
              className="mt-0.5 w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
              checked={fundamentals[item.key] || false}
              onChange={(e) => !disabled && onUpdate(item.key, e.target.checked)}
              disabled={disabled}
            />
            <div>
              <span className={`text-sm font-medium ${fundamentals[item.key] ? 'text-green-700' : 'text-gray-700'}`}>
                {item.label}
              </span>
              <p className="text-xs text-gray-500">{item.description}</p>
            </div>
          </label>
        ))}
      </div>

      {/* Send back to Sales - only show if something is missing */}
      {(!fundamentals.folder_created || !fundamentals.artwork_received) && (
        <div className="mt-4 pt-3 border-t border-gray-100">
          <button
            onClick={onSendBackToSales}
            className="w-full px-4 py-2 bg-orange-50 text-orange-700 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors text-sm font-medium flex items-center justify-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Email Sales about missing items
          </button>
        </div>
      )}
    </div>
  );
}

// Check if all required fundamentals are complete
function areFundamentalsComplete(fundamentals) {
  return fundamentals.kickoff_received && fundamentals.folder_created && fundamentals.artwork_received;
}

// Detail panel component (right side)
function DetailPanel({ project, projectDetail, loading, onAssign, onGenerateWO, assigning, generating, fundamentals, onUpdateFundamental, fundamentalsLoading, onSendBackToSales }) {
  if (!project) {
    return (
      <div className="flex-1 flex items-center justify-center text-gray-400">
        <div className="text-center">
          <div className="text-4xl mb-2">👈</div>
          <p>Select a project to view details</p>
        </div>
      </div>
    );
  }

  const detail = projectDetail || {};
  const daysLeft = getDaysUntilShip(project.shipDate);
  const canAssign = areFundamentalsComplete(fundamentals);

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-white">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              Quote {project.quoteNumber}
            </h2>
            <p className="text-gray-600">{project.customerName}</p>
          </div>
          {project.assignedPmName && (
            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
              PM: {project.assignedPmName}
            </span>
          )}
        </div>
      </div>

      {/* Project Info */}
      <div className="p-4 space-y-4">
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 mb-3">Project Details</h3>
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-gray-500">Project Name</dt>
              <dd className="font-medium text-gray-900">{project.projectName || "—"}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Ship Date</dt>
              <dd className={`font-medium ${
                daysLeft !== null && daysLeft < 0 ? "text-red-600" :
                daysLeft !== null && daysLeft <= 3 ? "text-orange-600" :
                "text-gray-900"
              }`}>
                {project.shipDate ? formatDate(project.shipDate) : "Not set"}
                {daysLeft !== null && (
                  <span className="text-xs ml-1">
                    ({daysLeft < 0 ? `${Math.abs(daysLeft)}d overdue` : `${daysLeft}d`})
                  </span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">Sales Rep</dt>
              <dd className="font-medium text-gray-900">{project.salesRep || "—"}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Total Value</dt>
              <dd className="font-medium text-gray-900">{formatCurrency(project.totalValue)}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Orders</dt>
              <dd className="font-medium text-gray-900">
                {project.orderCount > 0 ? (
                  <span className="text-green-600">{project.orderCount} order(s)</span>
                ) : (
                  <span className="text-orange-500">No orders yet</span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">SKUs</dt>
              <dd className="font-medium text-gray-900">{project.totalSkus || 0}</dd>
            </div>
          </dl>
        </div>

        {/* SKU List */}
        {loading ? (
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="animate-pulse flex items-center gap-2">
              <div className="w-4 h-4 bg-gray-300 rounded-full"></div>
              <div className="h-4 bg-gray-300 rounded w-32"></div>
            </div>
          </div>
        ) : detail.skus && detail.skus.length > 0 ? (
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-3">
              SKUs ({detail.skus.length})
            </h3>
            <div className="space-y-2">
              {detail.skus.map((sku, idx) => (
                <div key={idx} className="bg-white rounded p-2 text-sm border border-gray-200">
                  <div className="flex justify-between">
                    <span className="font-mono text-purple-600">{sku.sku || `SKU ${idx + 1}`}</span>
                    <span className="text-gray-500">{sku.quantity?.toLocaleString()} qty</span>
                  </div>
                  <div className="text-gray-600 truncate">{sku.description}</div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {/* Fundamentals Checklist - show for unassigned projects */}
        {!project.assignedPmId && (
          <FundamentalsChecklist
            fundamentals={fundamentals}
            onUpdate={onUpdateFundamental}
            disabled={fundamentalsLoading}
            onSendBackToSales={onSendBackToSales}
          />
        )}

        {/* Actions */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 mb-3">Actions</h3>
          <div className="flex flex-wrap gap-2">
            {/* Assign to Me - show if no PM assigned, enabled only if fundamentals complete */}
            {!project.assignedPmId && (
              <button
                onClick={onAssign}
                disabled={assigning || !canAssign}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  canAssign
                    ? 'bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
                title={canAssign ? 'Assign this project to yourself' : 'Complete the fundamentals checklist first'}
              >
                {assigning ? "Assigning..." : "Assign to Me"}
              </button>
            )}

            {/* Generate WO - show if has order */}
            {project.orderNumber && (
              <button
                onClick={onGenerateWO}
                disabled={generating}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
              >
                {generating ? "Generating..." : "Generate Work Order"}
              </button>
            )}

            {/* View in JobBoss placeholder */}
            <button
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              onClick={() => alert("JobBoss integration coming soon")}
            >
              View in JobBoss
            </button>
          </div>

          {/* Status messages */}
          {!project.assignedPmId && !canAssign && (
            <p className="mt-3 text-sm text-yellow-600">
              Complete the fundamentals checklist above to enable assignment.
            </p>
          )}
          {!project.orderNumber && project.bucket !== 'pending' && (
            <p className="mt-3 text-sm text-orange-600">
              No order exists yet. Work order can be generated once an order is created in JobBoss.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// Main component
export default function PMPipelineV2({ acquireAccessToken }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    ready_to_work: true,
    won_awaiting_kickoff: true,
    pending: false,
  });
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectDetail, setProjectDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Fundamentals state
  const [fundamentals, setFundamentals] = useState({
    kickoff_received: false,
    folder_created: false,
    artwork_received: false,
  });
  const [fundamentalsLoading, setFundamentalsLoading] = useState(false);

  // Load pipeline data
  const loadPipeline = useCallback(async () => {
    try {
      setLoading(true);
      const accessToken = await acquireAccessToken();
      if (!accessToken) {
        setError("Authentication required");
        return;
      }

      // Only fetch unassigned projects (Tab 1: Pipeline)
      // Projects with assigned_pm_id will appear in other lifecycle tabs
      const res = await fetch("/api/pm-center/pipeline/v2?unassignedOnly=true", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!res.ok) throw new Error(`Failed to load: ${res.status}`);

      const result = await res.json();
      setData(result);
      setError(null);
    } catch (err) {
      console.error("Error loading pipeline:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [acquireAccessToken]);

  // Load project detail and fundamentals
  const loadProjectDetail = useCallback(async (quoteNumber) => {
    try {
      setDetailLoading(true);
      setFundamentalsLoading(true);
      const accessToken = await acquireAccessToken();
      if (!accessToken) return;

      // Fetch project detail and fundamentals in parallel
      const [detailRes, fundamentalsRes] = await Promise.all([
        fetch(`/api/pm-center/pipeline/v2/project/${quoteNumber}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
        fetch(`/api/pm-center/projects/${quoteNumber}/fundamentals`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
      ]);

      if (detailRes.ok) {
        const result = await detailRes.json();
        setProjectDetail(result);
      }

      if (fundamentalsRes.ok) {
        const fundData = await fundamentalsRes.json();
        setFundamentals(fundData.fundamentals || {
          kickoff_received: false,
          folder_created: false,
          artwork_received: false,
        });
      } else {
        // Reset to defaults if no fundamentals exist
        setFundamentals({
          kickoff_received: false,
          folder_created: false,
          artwork_received: false,
        });
      }
    } catch (err) {
      console.error("Error loading project detail:", err);
    } finally {
      setDetailLoading(false);
      setFundamentalsLoading(false);
    }
  }, [acquireAccessToken]);

  // Update a single fundamental
  const updateFundamental = useCallback(async (field, value) => {
    if (!selectedProject) return;

    // Optimistic update
    setFundamentals(prev => ({ ...prev, [field]: value }));

    try {
      const accessToken = await acquireAccessToken();
      if (!accessToken) return;

      const res = await fetch(`/api/pm-center/projects/${selectedProject.quoteNumber}/fundamentals`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ [field]: value }),
      });

      if (!res.ok) {
        // Revert on error
        setFundamentals(prev => ({ ...prev, [field]: !value }));
      }
    } catch (err) {
      // Revert on error
      console.error("Error updating fundamental:", err);
      setFundamentals(prev => ({ ...prev, [field]: !value }));
    }
  }, [acquireAccessToken, selectedProject]);

  // Handle project selection
  const handleSelectProject = (project) => {
    setSelectedProject(project);
    setProjectDetail(null);
    setFundamentals({ kickoff_received: false, folder_created: false, artwork_received: false });
    loadProjectDetail(project.quoteNumber);
  };

  // Send back to sales - auto-detect missing fundamentals and generate email
  const handleSendBackToSales = useCallback(async () => {
    if (!selectedProject) return;

    // Auto-detect what's missing (only folder and artwork - kickoff is PM's confirmation)
    const missingItems = [];
    if (!fundamentals.folder_created) {
      missingItems.push("Project folder created on server");
    }
    if (!fundamentals.artwork_received) {
      missingItems.push("Artwork files saved in the project folder");
    }

    if (missingItems.length === 0) {
      alert("All fundamentals are checked. No need to send back to sales.");
      return;
    }

    // Build email content
    const salesRep = selectedProject.salesRep || "Sales";
    const customerName = selectedProject.customerName || "the customer";
    const quoteNumber = selectedProject.quoteNumber;
    const projectName = selectedProject.projectName || "";

    const subject = `Action Required: Missing items for Quote ${quoteNumber} - ${customerName}`;
    const body = `Hi ${salesRep},

I'm reviewing Quote ${quoteNumber} for ${customerName}${projectName ? ` (${projectName})` : ""} and need the following before I can proceed:

${missingItems.map(item => `  - ${item}`).join("\n")}

Please complete these items and let me know when ready.

Thanks!`;

    // Confirm and open email
    const confirmed = confirm(
      `Send email to ${salesRep} about missing items?\n\n` +
      `Missing:\n${missingItems.map(item => `  - ${item}`).join("\n")}\n\n` +
      `Click OK to open your email client.`
    );

    if (!confirmed) return;

    // Open mailto link
    const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoUrl, "_blank");

    // Also log to backend
    try {
      const accessToken = await acquireAccessToken();
      if (!accessToken) return;

      await fetch(`/api/pm-center/projects/${selectedProject.quoteNumber}/send-to-sales`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          reason: `Missing: ${missingItems.join(", ")}`,
          missingItems,
        }),
      });
    } catch (err) {
      console.error("Failed to log send-to-sales:", err);
    }
  }, [acquireAccessToken, selectedProject, fundamentals]);

  // Handle assign to me
  const handleAssign = async () => {
    if (!selectedProject) return;

    try {
      setAssigning(true);
      const accessToken = await acquireAccessToken();
      if (!accessToken) return;

      const res = await fetch(`/api/pm-center/projects/${selectedProject.quoteNumber}/assign`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          orderNumber: selectedProject.orderNumber,
          customerName: selectedProject.customerName,
          projectName: selectedProject.description || selectedProject.projectName,
          shipDate: selectedProject.shipDate,
          salesRep: selectedProject.salesRep,
        }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to assign");
      }

      alert(`Project ${selectedProject.quoteNumber} assigned to you!`);

      // Refresh data
      await loadPipeline();
      await loadProjectDetail(selectedProject.quoteNumber);
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setAssigning(false);
    }
  };

  // Handle generate work order
  const handleGenerateWO = async () => {
    if (!selectedProject || !selectedProject.orderNumber) return;

    try {
      setGenerating(true);
      const accessToken = await acquireAccessToken();
      if (!accessToken) return;

      const res = await fetch("/api/pm-center/work-orders/generate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          orderNumber: selectedProject.orderNumber,
          quoteNumber: selectedProject.quoteNumber,
        }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to generate");
      }

      const result = await res.json();
      alert(`Work Order generated!\n\nFile: ${result.fileName}`);

      if (result.sharepointUrl) {
        await navigator.clipboard.writeText(result.sharepointUrl);
        alert("SharePoint URL copied to clipboard");
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setGenerating(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadPipeline();
  }, [loadPipeline]);

  // Auto-refresh every 60s
  useEffect(() => {
    const interval = setInterval(loadPipeline, 60000);
    return () => clearInterval(interval);
  }, [loadPipeline]);

  // Toggle section
  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  if (loading && !data) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading pipeline...</p>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={loadPipeline}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex overflow-hidden">
      {/* Left Panel - Project List */}
      <div className="w-96 flex-shrink-0 border-r border-gray-200 flex flex-col bg-white overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">Pipeline</h2>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded">{data?.totals?.readyToWork || 0} ready</span>
              <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded">{data?.totals?.awaitingKickoff || 0} awaiting</span>
              <button
                onClick={loadPipeline}
                className="p-1 hover:bg-gray-200 rounded"
                title="Refresh"
              >
                <svg className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Sections */}
        <div className="flex-1 overflow-y-auto">
          {data?.sections?.map(section => (
            <div key={section.id} className="border-b border-gray-100">
              <SectionHeader
                section={section}
                expanded={expandedSections[section.id]}
                onToggle={() => toggleSection(section.id)}
              />
              {expandedSections[section.id] && (
                <div className="divide-y divide-gray-100">
                  {section.projects.length === 0 ? (
                    <div className="px-4 py-6 text-center text-gray-400 text-sm">
                      No projects in this section
                    </div>
                  ) : (
                    section.projects.map(project => (
                      <ProjectCard
                        key={project.quoteNumber}
                        project={project}
                        isSelected={selectedProject?.quoteNumber === project.quoteNumber}
                        onClick={() => handleSelectProject(project)}
                      />
                    ))
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Right Panel - Detail */}
      <div className="flex-1 bg-gray-50 flex flex-col overflow-hidden">
        <DetailPanel
          project={selectedProject}
          projectDetail={projectDetail}
          loading={detailLoading}
          onAssign={handleAssign}
          onGenerateWO={handleGenerateWO}
          assigning={assigning}
          generating={generating}
          fundamentals={fundamentals}
          onUpdateFundamental={updateFundamental}
          fundamentalsLoading={fundamentalsLoading}
          onSendBackToSales={handleSendBackToSales}
        />
      </div>
    </div>
  );
}
